// FILE: components/sections/ComponentLibrarySection.tsx
// This section provides a comprehensive overview of all available components.

import React from 'react';
import { SectionContainer, SubSection } from '../Content';
import { Breadcrumb } from '../Breadcrumb';
import CodeBlock from '../CodeBlock';

export const ComponentLibrarySection: React.FC<{ groupTitle: string; sectionTitle: string; id: string; }> = ({ groupTitle, sectionTitle, id }) => {
    const componentCategories = [
        {
            title: "Core UI Components",
            description: "Essential building blocks for user interfaces",
            components: [
                { name: 'Button', usage: '<Button>', description: 'Interactive buttons with multiple variants' },
                { name: 'Input', usage: '<Input>', description: 'Text input fields with validation support' },
                { name: 'Card', usage: '<Card>', description: 'Content containers with flexible layouts' },
                { name: 'Badge', usage: '<Badge>', description: 'Status indicators and labels' },
                { name: 'Avatar', usage: '<Avatar>', description: 'User profile images and initials' },
                { name: 'Icon', usage: '<Icon>', description: 'Material Design icon system' },
            ]
        },
        {
            title: "Form Components",
            description: "Components for building user input forms",
            components: [
                { name: 'Checkbox', usage: '<Checkbox>', description: 'Single and multi-selection checkboxes' },
                { name: 'Radio', usage: '<Radio>', description: 'Single selection radio buttons' },
                { name: 'Select', usage: '<Select>', description: 'Dropdown selection components' },
                { name: 'Textarea', usage: '<Textarea>', description: 'Multi-line text input' },
                { name: 'ToggleSwitch', usage: '<ToggleSwitch>', description: 'On/off toggle switches' },
                { name: 'Slider', usage: '<Slider>', description: 'Range selection sliders' },
                { name: 'DatePicker', usage: '<DatePicker>', description: 'Date selection component' },
                { name: 'DateRangePicker', usage: '<DateRangePicker>', description: 'Date range selection' },
                { name: 'FileUploader', usage: '<FileUploader>', description: 'File upload with drag & drop' },
                { name: 'PasswordInput', usage: '<PasswordInput>', description: 'Password field with visibility toggle' },
                { name: 'Autocomplete', usage: '<Autocomplete>', description: 'Searchable input with suggestions' },
                { name: 'MultiSelect', usage: '<MultiSelect>', description: 'Multi-selection dropdown' },
            ]
        },
        {
            title: "Layout Components",
            description: "Components for structuring page layouts",
            components: [
                { name: 'Container', usage: '<Container>', description: 'Responsive content containers' },
                { name: 'Grid', usage: '<Grid>', description: 'Flexible grid layout system' },
                { name: 'Stack', usage: '<Stack>', description: 'Vertical and horizontal stacking' },
                { name: 'Layout', usage: '<Layout>', description: 'Page layout composition' },
                { name: 'LayoutHeader', usage: '<LayoutHeader>', description: 'Page header section' },
                { name: 'LayoutSidebar', usage: '<LayoutSidebar>', description: 'Page sidebar section' },
                { name: 'Main', usage: '<Main>', description: 'Main content area' },
                { name: 'Footer', usage: '<Footer>', description: 'Page footer section' },
                { name: 'Content', usage: '<Content>', description: 'Content wrapper component' },
                { name: 'TwoColumnLayout', usage: '<TwoColumnLayout>', description: 'Two-column page layout' },
                { name: 'ThreeColumnLayout', usage: '<ThreeColumnLayout>', description: 'Three-column page layout' },
            ]
        },
        {
            title: "Navigation Components",
            description: "Components for user navigation",
            components: [
                { name: 'Breadcrumb', usage: '<Breadcrumb>', description: 'Page navigation breadcrumbs' },
                { name: 'Header', usage: '<Header>', description: 'Application header navigation' },
                { name: 'Sidebar', usage: '<Sidebar>', description: 'Collapsible sidebar navigation' },
                { name: 'Navigation', usage: '<Navigation>', description: 'Primary navigation menu' },
                { name: 'MegaMenu', usage: '<MegaMenu>', description: 'Large dropdown navigation' },
                { name: 'Pagination', usage: '<Pagination>', description: 'Page navigation controls' },
                { name: 'Stepper', usage: '<Stepper>', description: 'Step-by-step progress indicator' },
            ]
        },
        {
            title: "Data Display Components",
            description: "Components for presenting data and content",
            components: [
                { name: 'Table', usage: '<Table>', description: 'Data table with sorting and pagination' },
                { name: 'VirtualizedTable', usage: '<VirtualizedTable>', description: 'High-performance table for large datasets' },
                { name: 'TreeView', usage: '<TreeView>', description: 'Hierarchical data display' },
                { name: 'Accordion', usage: '<Accordion>', description: 'Collapsible content sections' },
                { name: 'Progress', usage: '<Progress>', description: 'Progress bars and indicators' },
                { name: 'Skeleton', usage: '<Skeleton>', description: 'Loading state placeholders' },
                { name: 'Spinner', usage: '<Spinner>', description: 'Loading spinners and indicators' },
                { name: 'Calendar', usage: '<Calendar>', description: 'Calendar date display' },
                { name: 'CalendarGridView', usage: '<CalendarGridView>', description: 'Grid-based calendar view' },
            ]
        },
        {
            title: "Feedback Components",
            description: "Components for user feedback and notifications",
            components: [
                { name: 'Alert', usage: '<Alert>', description: 'Information and warning messages' },
                { name: 'Banner', usage: '<Banner>', description: 'Prominent notification banners' },
                { name: 'Toast', usage: '<Toast>', description: 'Temporary notification toasts' },
                { name: 'Modal', usage: '<Modal>', description: 'Overlay dialogs and forms' },
                { name: 'Drawer', usage: '<Drawer>', description: 'Slide-out side panels' },
                { name: 'Popover', usage: '<Popover>', description: 'Contextual information popups' },
                { name: 'Tooltip', usage: '<Tooltip>', description: 'Hover information tooltips' },
                { name: 'EmptyState', usage: '<EmptyState>', description: 'Empty data state messages' },
            ]
        },
        {
            title: "Utility Components",
            description: "Helper components for enhanced functionality",
            components: [
                { name: 'ButtonGroup', usage: '<ButtonGroup>', description: 'Grouped button collections' },
                { name: 'SplitButton', usage: '<SplitButton>', description: 'Button with dropdown actions' },
                { name: 'Dropdown', usage: '<Dropdown>', description: 'Dropdown menu components' },
                { name: 'BrowserCompatibility', usage: '<BrowserCompatibility>', description: 'Browser support information' },
                { name: 'SectionContainer', usage: '<SectionContainer>', description: 'Section wrapper component' },
                { name: 'SubSection', usage: '<SubSection>', description: 'Subsection wrapper component' },
                { name: 'ComponentPreview', usage: '<ComponentPreview>', description: 'Component demonstration wrapper' },
                { name: 'PropsTable', usage: '<PropsTable>', description: 'Component props documentation' },
                { name: 'CodeBlock', usage: '<CodeBlock>', description: 'Syntax-highlighted code display' },
            ]
        }
    ];

    return (
        <SectionContainer id={id}>
            <Breadcrumb group={groupTitle} section={sectionTitle} />
            <h2 className="section-title">Component Library</h2>
            <p className="section-subtitle">
                The Acutrack Design System provides a comprehensive collection of 60+ reusable components, organized by functionality and purpose. Each component is built with accessibility, performance, and developer experience in mind.
            </p>
            
            <div className="space-y-8">
                {componentCategories.map((category, categoryIndex) => (
                    <SubSection key={categoryIndex} title={category.title}>
                        <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                            {category.description}
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {category.components.map((component, componentIndex) => (
                                <div key={componentIndex} className="bg-bg-secondary dark:bg-dark-bg-secondary p-4 rounded-lg border border-border-color dark:border-dark-border-color hover:border-brand-orange/50 dark:hover:border-dark-brand-orange/50 transition-colors">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">
                                        {component.name}
                                    </h4>
                                    <p className="text-text-secondary dark:text-dark-text-secondary text-xs mb-3">
                                        {component.description}
                                    </p>
                                    <CodeBlock code={component.usage} />
                                </div>
                            ))}
                        </div>
                    </SubSection>
                ))}
            </div>
            
            <div className="mt-8 p-6 bg-brand-orange/10 dark:bg-dark-brand-orange/10 rounded-lg border border-brand-orange/20 dark:border-dark-brand-orange/20">
                <h4 className="font-bold text-brand-orange dark:text-dark-brand-orange mb-2">
                    🎯 Component Usage
                </h4>
                <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
                    All components are available for import from the main package. Use the interactive workbenches in each component section to configure props and get production-ready code snippets.
                </p>
            </div>
        </SectionContainer>
    );
};
